import { DocumentReference, Timestamp, collection, doc, serverTimestamp, setDoc, updateDoc, writeBatch } from "firebase/firestore";
import { Timestamp as LiteTimestamp, where as liteWhere, type QueryConstraint } from "firebase/firestore/lite";
import db from "../Firebase.js";
import { Users } from "../../models/User.tsx";
import { BetDetails, ParlaySelection } from "../../models/MyBetModel.tsx";
import { UserActivity, UserIPData } from "../../models/UserActivity.tsx";
import {
    DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS,
    DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS,
    DEFAULT_ALLOW_FREE_PLAY,
    getWagerLimitsPreset,
    type WagerLimitsPreset,
} from "../player/SportService.tsx";
import {
    chunkArray,
    getVisibleUserNamesFromRoster,
    resolveHierarchyRoster,
} from "../../helpers/hierarchyRoster.ts";
import { requestAgentSummaryRefresh } from "../../helpers/agentSummaryRefresh.tsx";
import { getLiteDoc, getLiteQueryDocs } from "../../helpers/firestoreLiteQuery.ts";
import { DEFAULT_WEEKLY_ACCOUNT_VIEW } from "../../helpers/weeklyFiguresSelectors.tsx";

export type Bet = BetDetails | ParlaySelection;

/** Firestore allows at most 500 operations per writeBatch. */
const FIRESTORE_BATCH_LIMIT = 500;
const FIRESTORE_IN_QUERY_LIMIT = 30;

export const MAX_BULK_PLAYER_CREATE = 450;

export type BulkPlayerDraft = {
    userName: string;
    pin: string;
    parent?: string;
    master?: string;
};

export type BulkAddUsersResult = {
    created: Array<{ userName: string; pin: string; id: string }>;
    skipped: Array<{ userName: string; reason: string }>;
    error?: string;
};



export const setUserIdentity = async (docId: string, uid: string) => {
    const userRef = doc(db, `users/${docId}`);
    await updateDoc(userRef, { uid });
}


export async function getUserById(docId: string): Promise<Users | null> {
    try {
        const docSnap = await getLiteDoc("users", docId);

        if (!docSnap.exists()) {
            return null;
        }

        return {
            id: docSnap.id,
            ...(docSnap.data() as Users),
        };
    } catch (error) {
        console.error("Error obtaining user:", error);
        return null;
    }
}

export async function getSessionInfo(sessionId: string): Promise<Users> {
    localStorage.setItem('_sessionId', sessionId)

    const docs = await getLiteQueryDocs('users', [
        liteWhere('sessionId', '==', sessionId),
    ]);

    if (docs.length === 0) {
        throw new Error('Shame on you');
    }
    const updatedUserData = docs[0].data() as Users;

    return updatedUserData;
}


export function normalizeUserName(userName: string): string {
    return userName.trim().replace(/\s+/g, ' ').toUpperCase();
}

export function generateRandomPin(length = 5): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let pin = "";
    for (let i = 0; i < length; i++) {
        pin += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pin.toUpperCase();
}

export function generateNumberedUserNames(
    prefix: string,
    start: number,
    count: number,
    padLength?: number,
): string[] {
    const normalizedPrefix = normalizeUserName(prefix);
    const safeStart = Math.max(0, Math.floor(start) || 1);
    const safeCount = Math.max(0, Math.floor(count) || 0);
    const width = Math.max(1, Math.floor(padLength ?? 1) || 1);

    return Array.from({ length: safeCount }, (_, index) => {
        const number = String(safeStart + index);
        const suffix = width > 1 ? number.padStart(width, '0') : number;
        return `${normalizedPrefix}${suffix}`;
    });
}

export async function isUsernameInUse(userName: string): Promise<boolean> {
    const normalized = normalizeUserName(userName);
    if (!normalized) return false;

    const docs = await getLiteQueryDocs('users', [
        liteWhere('userName', '==', normalized),
    ]);
    return docs.length > 0;
}

export async function findUserNamesInUse(userNames: string[]): Promise<Set<string>> {
    const unique = [...new Set(userNames.map(normalizeUserName).filter(Boolean))];
    if (unique.length === 0) return new Set();

    const used = new Set<string>();
    const chunks = chunkArray(unique, FIRESTORE_IN_QUERY_LIMIT);
    const snapshots = await Promise.all(
        chunks.map(chunk =>
            getLiteQueryDocs('users', [liteWhere('userName', 'in', chunk)])
        )
    );

    snapshots.flat().forEach(docSnap => {
        const name = normalizeUserName((docSnap.data() as Users).userName || '');
        if (name) used.add(name);
    });

    return used;
}

function applyNewUserDefaults(newUser: Partial<Users>, preset?: WagerLimitsPreset): Partial<Users> {
    const user: Partial<Users> = {
        ...newUser,
        userName: normalizeUserName(newUser.userName || ''),
        pin: (newUser.pin || '').toUpperCase(),
        isOnline: true,
        lastLogin: Timestamp.now(),
        available: 0.00,
        pending: 0.00,
        img: '',
        email: '',
        isLogin: false,
        messaging: true,
        wagerLimit: 0,
        freePlay: 0,
        balance: 0,
        status: "Active",
        isActive: "Active",
        balanceRolls: "zeroBalance",
    };

    if (user.userType === 'Player') {
        user.available = 2000;
        user.creditLimit = 2000;
        user.casinoEnabled = false;
        if (preset) {
            user.regularBetMaxWager = preset.regularBetMaxWager;
            user.parlayMaxWager = preset.parlayMaxWager;
            user.teaserMaxWager = preset.teaserMaxWager;
            user.liveMaxWager = preset.liveMaxWager;
            user.propsMaxWager = preset.propsMaxWager;
            user.contestMaxWager = preset.contestMaxWager;
            user.maxPayout = preset.maxPayout ?? 0;
            user.maxFreePlayPayout = preset.maxFreePlayPayout ?? 0;
            user.maxFreePlayOdds = preset.maxFreePlayOdds ?? 0;
            user.maxOverallOdds = preset.maxOverallOdds ?? 0;
        } else {
            user.maxPayout = 0;
            user.maxFreePlayPayout = 0;
            user.maxFreePlayOdds = 0;
            user.maxOverallOdds = 0;
        }
    }

    if (user.userType === 'subAgent' || user.userType === 'masterAgent') {
        user.filters = {
            password: true,
            settleFigure: false,
            balanceForwardCarry: true,
            dailyWinLoss: true,
            endOfWeekBalance: true,
            pendingBalance: true,
            depositsWithdrawals: false,
            lastWager: false,
            lastLogin: false,
            bigFiguresMin: 500,
            agentID: true,
            timeAccepted: true,
            timeScheduled: false,
            creditLimit: true,
            settleLimit: false,
            wagerLimit: true,
            email: false,
            avaliable: false,
            pendingAmount: 'any',
            pendingType: 'allTypes',
            ...user.filters,
            accountView: DEFAULT_WEEKLY_ACCOUNT_VIEW,
            pendingTime: 'today',
        };
        user.agentMaxPayout = 0;
        user.agentMaxFreePlayPayout = 0;
        user.agentMaxFreePlayOdds = 0;
        user.agentMaxOverallOdds = 0;
        user.agentRegularBetMaxWager = 0;
        user.agentParlayMaxWager = 0;
        user.agentTeaserMaxWager = 0;
        user.agentLiveMaxWager = 0;
        user.agentPropsMaxWager = 0;
        user.agentContestMaxWager = 0;
    }

    if (user.userType === 'subAgent') {
        user.allowFreePlay = DEFAULT_ALLOW_FREE_PLAY;
        user.allowAccessToAllLimits = DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS;
        user.allowCreditDebitAdjustments = DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS;
    }

    return user;
}

export async function addUser(newUser: Partial<Users>): Promise<void> {
    if (!newUser.userName?.trim()) {
        throw new Error('Username is required');
    }

    const userName = normalizeUserName(newUser.userName);
    if (await isUsernameInUse(userName)) {
        throw new Error('Username is already in use');
    }

    const preset = newUser.userType === 'Player'
        ? await getWagerLimitsPreset(newUser.parent)
        : undefined;
    const payload = applyNewUserDefaults({ ...newUser, userName }, preset);

    const usersRef = collection(db, 'users');
    const docRef = doc(usersRef);
    await setDoc(docRef, { ...payload, id: docRef.id });

    // New players/agents must drop the hierarchy roster cache immediately.
    requestAgentSummaryRefresh();
}

export async function addUsers(newUsers: BulkPlayerDraft[]): Promise<BulkAddUsersResult> {
    const skipped: BulkAddUsersResult['skipped'] = [];
    const pending: BulkPlayerDraft[] = [];
    const seen = new Set<string>();

    for (const draft of newUsers) {
        const userName = normalizeUserName(draft.userName || '');
        const pin = (draft.pin || '').trim().toUpperCase();

        if (!userName) {
            skipped.push({ userName: draft.userName || '', reason: 'Username is required' });
            continue;
        }
        if (!pin) {
            skipped.push({ userName, reason: 'Password is required' });
            continue;
        }
        if (seen.has(userName)) {
            skipped.push({ userName, reason: 'Duplicate in this list' });
            continue;
        }

        seen.add(userName);
        pending.push({ ...draft, userName, pin });
    }

    if (pending.length > MAX_BULK_PLAYER_CREATE) {
        pending.splice(MAX_BULK_PLAYER_CREATE).forEach(extra => {
            skipped.push({ userName: extra.userName, reason: `Limit is ${MAX_BULK_PLAYER_CREATE} players per batch` });
        });
    }

    const usedNames = await findUserNamesInUse(pending.map(user => user.userName));
    const toCreate = pending.filter(user => {
        if (usedNames.has(user.userName)) {
            skipped.push({ userName: user.userName, reason: 'Username is already in use' });
            return false;
        }
        return true;
    });

    const created: BulkAddUsersResult['created'] = [];
    if (toCreate.length === 0) {
        return { created, skipped };
    }

    const presetsByParent = new Map<string, WagerLimitsPreset>();
    const uniqueParents = [...new Set(toCreate.map(user => user.parent || ''))];
    await Promise.all(uniqueParents.map(async parent => {
        presetsByParent.set(parent, await getWagerLimitsPreset(parent || undefined));
    }));

    const usersRef = collection(db, 'users');
    const batch = writeBatch(db);

    try {
        toCreate.forEach(draft => {
            const docRef = doc(usersRef);
            const payload = applyNewUserDefaults({
                userName: draft.userName,
                pin: draft.pin,
                parent: draft.parent,
                master: draft.master,
                userType: 'Player',
            }, presetsByParent.get(draft.parent || ''));

            batch.set(docRef, { ...payload, id: docRef.id });
            created.push({ userName: draft.userName, pin: draft.pin, id: docRef.id });
        });

        await batch.commit();
    } catch (error: any) {
        return {
            created: [],
            skipped,
            error: error?.message || 'Error creating players. Please try again.',
        };
    }

    requestAgentSummaryRefresh();
    return { created, skipped };
}

export async function searchMasterAgent(queryText: string, userType?: 'masterAgent' | 'subAgent' | 'Player', parent?: string): Promise<Users[]> {
    try {
        const constraints: QueryConstraint[] = [];
        if (userType) constraints.push(liteWhere('userType', '==', userType));
        if (userType && parent) constraints.push(liteWhere('parent', '==', parent));

        const querySnapshot = await getLiteQueryDocs('users', constraints);
        const users: Users[] = [];

        querySnapshot.forEach((docSnap) => {
            const userData = docSnap.data();
            if (userData) {
                users.push({ id: docSnap.id, ...userData } as Users);
            }
        });

        const filteredUsers = users.filter(user =>
            user.userName.toLowerCase().includes(queryText.toLowerCase())
        );

        return filteredUsers;
    } catch (error) {
        throw error;
    }
}

export async function userSearch(username: string): Promise<{ bets: Bet[] }> {
    let bets: Bet[] = [];

    const userDocs = await getLiteQueryDocs("users", [
        liteWhere("userName", "==", username),
    ]);
    const userData = userDocs[0].data();

    if (userData.userType === "subAgent") {
        const betsSnapshot = await getLiteQueryDocs('myBet', [
            liteWhere('player.parent', '==', userData.userName),
            liteWhere('betStatus', '==', 'pending'),
        ]);
        bets = betsSnapshot.map(docSnap => {
            const betData = docSnap.data() as Bet;
            return { ...betData } as Bet;
        });
    }

    if (userData.userType === "Player") {
        const betsSnapshot = await getLiteQueryDocs('myBet', [
            liteWhere('player.userName', '==', userData.userName),
            liteWhere('betStatus', '==', 'pending'),
        ]);
        bets = betsSnapshot.map(docSnap => {
            const betData = docSnap.data() as Bet;
            return { ...betData } as Bet;
        });
    }
    return { bets };
};


export async function showPlayers2(user: Users): Promise<Users[]> {
    if (user.userType !== 'masterAgent' && user.userType !== 'subAgent') return [];
    const roster = await resolveHierarchyRoster(user);
    return roster.players;
}

export async function showPlayers(user: Users): Promise<(Users & { playersUserNames: { userName: string }[]; id: string })[]> {
    if (user.userType !== 'masterAgent') return [];

    const roster = await resolveHierarchyRoster(user);
    const playersByAgent = new Map<string, { userName: string }[]>();
    roster.agentScope.forEach(n => playersByAgent.set(n, []));

    roster.players.forEach(player => {
        if (!player.parent) return;
        const list = playersByAgent.get(player.parent);
        if (list) list.push({ userName: player.userName });
    });

    return roster.subAgents.map(agent => ({
        ...agent,
        id: agent.id!,
        playersUserNames: playersByAgent.get(agent.userName) ?? [],
    }));
}

export async function editUser(userId: string, newData: Partial<Users>): Promise<void> {
    try {
        const UserDoc = doc(db, 'users', userId);
        await updateDoc(UserDoc, { ...newData, lastUpdated: serverTimestamp() });
    } catch (error) {
        throw error;
    }
}

/**
 * Applies the same partial update to many users with writeBatch (chunks of 500).
 * Prefer this over Promise.all(editUser) when updating a large player set.
 * Batch must be created inside each chunk — Firestore allows max 500 ops per commit.
 */
export async function batchEditUsers(
    userIds: string[],
    newData: Partial<Users>,
): Promise<void> {
    const ids = userIds.filter(Boolean);
    if (ids.length === 0) return;

    const payload = { ...newData, lastUpdated: serverTimestamp() };

    for (let i = 0; i < ids.length; i += FIRESTORE_BATCH_LIMIT) {
        const chunk = ids.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const batch = writeBatch(db);
        chunk.forEach(userId => {
            batch.update(doc(db, 'users', userId), payload);
        });
        await batch.commit();
    }
}

export type FirestoreReadOptions = { fromServer?: boolean };

export async function showUser(userName: string, _options?: FirestoreReadOptions): Promise<Users> {
    try {
        const docs = await getLiteQueryDocs('users', [
            liteWhere('userName', '==', userName),
        ]);

        if (docs.length > 0) {
            const userDoc = docs[0];
            return {
                ...userDoc.data(),
                id: userDoc.id,
            } as Users;
        } else {
            throw new Error('User not found');
        }
    } catch (error) {
        throw error;
    }
}

export async function showUserById(userId: string, _options?: FirestoreReadOptions): Promise<Users> {
    try {
        const userDoc = await getLiteDoc('users', userId);
        if (!userDoc.exists()) {
            throw new Error('User not found');
        }
        return {
            ...userDoc.data(),
            id: userDoc.id,
        } as Users;
    } catch (error) {
        throw error;
    }
}

export async function searchSubAgents(queryText: string, parent: string): Promise<Users[]> {
    try {
        const roster = await resolveHierarchyRoster({
            userName: parent,
            userType: 'masterAgent',
        });
        if (!queryText.trim()) {
            return roster.subAgents;
        }
        const q = queryText.toLowerCase();
        return roster.subAgents.filter(user =>
            user.userName.toLowerCase().includes(q)
        );
    } catch (error) {
        throw error;
    }
}




export async function searchUsers(queryText: string, userType: 'masterAgent' | 'subAgent', parent: string): Promise<Users[]> {
    try {
        const roster = await resolveHierarchyRoster({ userName: parent, userType });
        const users: Users[] = userType === 'masterAgent'
            ? [...roster.subAgents, ...roster.players]
            : [...roster.players];
        const q = queryText.toLowerCase();

        return users.filter(user =>
            user.userName?.toLowerCase().includes(q) ||
            user.pin?.toLowerCase().includes(q)
            // || user.name?.toLowerCase().includes(q)
        );


    } catch (error: any) {
        throw new Error(error);
    }
}

export const logoutUser = async (userRef: DocumentReference, setUser: (user: Users | null) => void) => {
    await updateDoc(userRef, {
        isOnline: false,
        isLogin: false,
        sessionExpiration: null,
    });
    setUser(null);
};

const normalizeUserType = (t?: string | null) =>
    (t ?? "").trim().toLowerCase();

const normalizeStoredUserType = (t?: string | null) => normalizeUserType(t);

const getVisibleUsers = async (viewerUserName: string, viewerUserType: string): Promise<string[]> => {
    const viewerType = normalizeUserType(viewerUserType);

    if (viewerType === "player") {
        return [viewerUserName];
    }

    const resolvedType =
        viewerType === "subagent" ? "subAgent" :
            viewerType === "masteragent" ? "masterAgent" :
                "masterAgent";

    const roster = await resolveHierarchyRoster({
        userName: viewerUserName,
        userType: resolvedType,
    });

    if (resolvedType === "subAgent") {
        return roster.players.map(p => p.userName);
    }

    return getVisibleUserNamesFromRoster(roster);
};

const normalizeIpData = (raw: unknown): UserIPData | undefined => {
    if (!raw || typeof raw !== "object") return undefined;

    const data = raw as Record<string, unknown>;
    const lat = typeof data.lat === "number" ? data.lat : undefined;
    const lon = typeof data.lon === "number" ? data.lon : undefined;

    return {
        ip: (data.ip ?? data.query) as string | undefined,
        country: data.country as string | undefined,
        regionName: data.regionName as string | undefined,
        city: data.city as string | undefined,
        zip: data.zip as string | undefined,
        lat,
        lon,
        query: data.query as string | undefined,
    };
};

const normalizeUserActivityDoc = (
    id: string,
    data: Record<string, unknown>
): UserActivity => ({
    id,
    userName: String(data.userName ?? data.UserName ?? ""),
    dateTime: (data.dateTime ?? data.DateTime ?? data.date) as UserActivity["dateTime"],
    action: String(data.action ?? ""),
    IpData: normalizeIpData(data.IpData ?? data.ipData),
});

const matchesUserName = (left: string, right: string): boolean =>
    left.trim().toUpperCase() === right.trim().toUpperCase();

const getActivityMillis = (dateTime: UserActivity["dateTime"]): number => {
    if (dateTime && typeof dateTime.toMillis === "function") return dateTime.toMillis();
    if (dateTime && typeof dateTime.toDate === "function") return dateTime.toDate().getTime();
    if (dateTime && typeof (dateTime as { seconds?: number }).seconds === "number") {
        return (dateTime as { seconds: number }).seconds * 1000;
    }
    return 0;
};

const buildUserNameVariants = (userName: string): string[] =>
    [...new Set([userName.trim(), userName.trim().toUpperCase(), userName.trim().toLowerCase()].filter(Boolean))];

const resolveCanonicalUserName = async (userName: string): Promise<string | null> => {
    for (const attempt of buildUserNameVariants(userName)) {
        const snap = await getLiteQueryDocs("users", [
            liteWhere("userName", "==", attempt),
        ]);
        if (snap.length > 0) {
            return (snap[0].data() as Users).userName;
        }
    }
    return null;
};

const canViewerAccessTargetUser = async (
    targetUserName: string,
    viewerUserName: string,
    viewerUserType: string
): Promise<string | null> => {
    const viewerType = normalizeUserType(viewerUserType);
    const canonical = await resolveCanonicalUserName(targetUserName);
    if (!canonical) return null;

    const userSnap = await getLiteQueryDocs("users", [
        liteWhere("userName", "==", canonical),
    ]);
    if (userSnap.length === 0) return null;

    const target = userSnap[0].data() as Users;
    const targetType = normalizeStoredUserType(target.userType);

    if (viewerType === "player") {
        return matchesUserName(canonical, viewerUserName) ? canonical : null;
    }

    if (viewerType === "subagent") {
        if (targetType === "player" && target.parent === viewerUserName) return canonical;
        if (targetType === "subagent" && matchesUserName(canonical, viewerUserName)) return canonical;
        return null;
    }

    if (viewerType === "masteragent") {
        if (targetType === "subagent" && target.parent === viewerUserName) return canonical;
        if (targetType === "player" && target.parent) {
            const parentSnap = await getLiteQueryDocs("users", [
                liteWhere("userName", "==", target.parent),
                liteWhere("parent", "==", viewerUserName),
                liteWhere("userType", "==", "subAgent"),
            ]);
            if (parentSnap.length > 0) return canonical;
        }
    }

    return null;
};

// Nota: en algunos entornos los documentos usan claves legacy (UserName/DateTime/date),
// y Firestore no soporta OR nativo entre campos distintos. Para no “perder” data,
// consultamos por ambos campos de username y filtramos por rango en memoria usando
// el normalizador.
const fetchActivitySnapshotsForUserName = async (userName: string) => {
    const [userNameSnap, legacyUserNameSnap] = await Promise.all([
        getLiteQueryDocs("userActivity", [liteWhere("userName", "==", userName)]),
        getLiteQueryDocs("userActivity", [liteWhere("UserName", "==", userName)]),
    ]);

    return [userNameSnap, legacyUserNameSnap];
};

const fetchActivitySnapshotsByDateRange = async (
    fieldName: "dateTime" | "DateTime" | "date",
    startTs: Timestamp,
    endTs: Timestamp
) => {
    return getLiteQueryDocs("userActivity", [
        liteWhere(fieldName, ">=", LiteTimestamp.fromMillis(startTs.toMillis())),
        liteWhere(fieldName, "<=", LiteTimestamp.fromMillis(endTs.toMillis())),
    ]);
};

const fetchActivityForUserNames = async (
    userNames: string[],
    startTs: Timestamp,
    endTs: Timestamp
): Promise<UserActivity[]> => {
    if (userNames.length === 0) return [];

    const queryNames = new Set<string>();
    userNames.forEach(name => {
        buildUserNameVariants(name).forEach(variant => queryNames.add(variant));
    });

    const snapshotGroups = await Promise.all(
        [...queryNames].map(name => fetchActivitySnapshotsForUserName(name))
    );

    const seen = new Set<string>();
    const activities: UserActivity[] = [];

    const startMs = startTs.toMillis();
    const endMs = endTs.toMillis();

    snapshotGroups.flat().forEach(docs => {
        docs.forEach(docSnap => {
            if (seen.has(docSnap.id)) return;

            const normalized = normalizeUserActivityDoc(
                docSnap.id,
                docSnap.data() as Record<string, unknown>
            );

            const ms = getActivityMillis(normalized.dateTime);
            if (!ms) return;
            if (ms < startMs || ms > endMs) return;

            seen.add(docSnap.id);
            activities.push(normalized);
        });
    });

    return activities;
};

const fetchVisibleActivityByDateRange = async (
    visibleUserNames: string[],
    startTs: Timestamp,
    endTs: Timestamp
): Promise<UserActivity[]> => {
    if (visibleUserNames.length === 0) return [];

    const visibleSet = new Set(visibleUserNames.map(n => n.trim().toUpperCase()).filter(Boolean));

    const [dateTimeSnap, legacyDateTimeSnap, legacyDateSnap] = await Promise.all([
        fetchActivitySnapshotsByDateRange("dateTime", startTs, endTs),
        fetchActivitySnapshotsByDateRange("DateTime", startTs, endTs),
        fetchActivitySnapshotsByDateRange("date", startTs, endTs),
    ]);

    const seen = new Set<string>();
    const activities: UserActivity[] = [];

    [dateTimeSnap, legacyDateTimeSnap, legacyDateSnap].forEach(docs => {
        docs.forEach(docSnap => {
            if (seen.has(docSnap.id)) return;

            const normalized = normalizeUserActivityDoc(
                docSnap.id,
                docSnap.data() as Record<string, unknown>
            );

            const normalizedUser = normalized.userName.trim().toUpperCase();
            if (!visibleSet.has(normalizedUser)) return;

            seen.add(docSnap.id);
            activities.push(normalized);
        });
    });

    return activities;
};

export const getUserActivityByDateRange = async (
    searchUserName: string | null,
    viewerUserName: string,
    viewerUserType: string,
    startDate: Date,
    endDate: Date
): Promise<UserActivity[]> => {
    try {
        const startTs = Timestamp.fromDate(startDate);
        const endTs = Timestamp.fromDate(endDate);

        let userNamesToQuery: string[] = [];

        if (searchUserName) {
            const allowedCanonical = await canViewerAccessTargetUser(
                searchUserName,
                viewerUserName,
                viewerUserType
            );
            if (!allowedCanonical) return [];
            userNamesToQuery = [allowedCanonical];

            const activities = await fetchActivityForUserNames(userNamesToQuery, startTs, endTs);
            return activities.sort((a, b) => getActivityMillis(b.dateTime) - getActivityMillis(a.dateTime));
        } else {
            userNamesToQuery = await getVisibleUsers(viewerUserName, viewerUserType);
            if (userNamesToQuery.length === 0) return [];

            const activities = await fetchVisibleActivityByDateRange(userNamesToQuery, startTs, endTs);
            return activities.sort((a, b) => getActivityMillis(b.dateTime) - getActivityMillis(a.dateTime));
        }

    } catch (error) {
        console.error("Error en getUserActivityByDateRange:", error);
        throw error;
    }
};





export async function searchCustomer(queryText: string, userType: string, parent?: string): Promise<Users[]> {
    try {
        if (!parent || (userType !== 'masterAgent' && userType !== 'subAgent')) {
            return [];
        }

        const roster = await resolveHierarchyRoster({
            userName: parent,
            userType: userType as 'masterAgent' | 'subAgent',
        });
        const users = userType === 'masterAgent'
            ? [...roster.subAgents, ...roster.players]
            : [...roster.players];

        return users.filter(user =>
            user.userName.toLowerCase().includes(queryText.toLowerCase())
        );
    } catch (error) {
        console.error('Error searching users:', error);
        throw error;
    }
}

export async function addBetToAvailable(
    userName: string,
    betAmount: number,
    options?: { isFreePlay?: boolean }
) {
    const docs = await getLiteQueryDocs("users", [
        liteWhere("userName", "==", userName),
    ]);

    if (docs.length === 0) throw new Error("User not found");

    const userDoc = docs[0];
    const userData = userDoc.data();
    const userRef = doc(db, "users", userDoc.id);

    if (options?.isFreePlay) {
        const currentFreePlay = userData.freePlay ?? 0;
        const newFreePlay = currentFreePlay + betAmount;
        await updateDoc(userRef, { freePlay: newFreePlay });
        return;
    }

    const currentAvailable = userData.available ?? 0;
    const currentPending = userData.pending ?? 0;
    await updateDoc(userRef, {
        available: currentAvailable + betAmount,
        pending: Math.max(0, currentPending - betAmount),
    });
}




